// src/api/botApi.js
import { apiFetch, getAuthToken } from "../apiClient";

export function getBotSettings(userId) {
    return apiFetch(`/api/bot/settings?user_id=${encodeURIComponent(userId)}`);
}

export function updateBotSettings(userId, patch) {
    return apiFetch(`/api/bot/settings?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, ...patch }),
    });
}

export function getBotStatus(userId) {
    return apiFetch(`/api/bot/status?user_id=${encodeURIComponent(userId)}`);
}

export function getBotLogs(userId, limit = 200) {
    return apiFetch(
        `/api/bot/logs?user_id=${encodeURIComponent(userId)}&limit=${limit}`
    );
}

export function runBotTick(userId) {
    return apiFetch(`/api/bot/run-tick`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
    });
}

export function killSwitch(userId, enabled = false) {
    return apiFetch(`/api/bot/kill-switch?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, enabled }),
    });
}

export function refreshBotState(userId) {
    return apiFetch(`/api/bot/state/refresh?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
    });
}

export function resetBotState(userId, clearPeak = false) {
    return apiFetch(`/api/bot/state/reset?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({
            user_id: userId,
            clear_peak: clearPeak,
        }),
    });
}
export function runSmartExit(userId, { mode = "paper", execute = false, debug = true } = {}) {
    return apiFetch(`/api/bot/smart-exit?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, mode, execute, debug }),
    });
}

// ---------------- Targeted Ticker Bot ----------------
export function getTargetBotSettings(userId) {
    return apiFetch(`/api/target-bot/settings?user_id=${encodeURIComponent(userId)}`);
}

export function updateTargetBotSettings(userId, patch) {
    return apiFetch(`/api/target-bot/settings?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, ...patch }),
    });
}

export function getTargetBotStatus(userId) {
    return apiFetch(`/api/target-bot/status?user_id=${encodeURIComponent(userId)}`);
}

export function getTargetBotLogs(userId, limit = 200) {
    return apiFetch(`/api/target-bot/logs?user_id=${encodeURIComponent(userId)}&limit=${limit}`);
}

export function runTargetBotTick(userId, { ignore_cooldown = false, execute = true, sell_only = false } = {}) {
    return apiFetch(`/api/target-bot/run-tick`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, ignore_cooldown, execute, sell_only }),
    });
}

export function resetTargetBotState(userId) {
    return apiFetch(`/api/target-bot/state/reset?user_id=${encodeURIComponent(userId)}`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId }),
    });
}


// ---------------- E*TRADE Broker ----------------
export function startEtradeOAuth(userId, mode = "sandbox") {
    return apiFetch(`/api/brokers/etrade/start`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, mode }),
    });
}

export function completeEtradeOAuth({ state, verifier }) {
    return apiFetch(`/api/brokers/etrade/callback`, {
        method: "POST",
        body: JSON.stringify({ state, verifier }),
    });
}

export function getEtradeStatus(userId, mode = "sandbox") {
    return apiFetch(`/api/brokers/etrade/status?user_id=${encodeURIComponent(userId)}&mode=${encodeURIComponent(mode)}`);
}

export function getEtradeAccount(userId, mode = "sandbox") {
    return apiFetch(`/api/brokers/etrade/account?user_id=${encodeURIComponent(userId)}&mode=${encodeURIComponent(mode)}`);
}

export function getEtradePositions(userId, mode = "sandbox") {
    return apiFetch(`/api/brokers/etrade/positions?user_id=${encodeURIComponent(userId)}&mode=${encodeURIComponent(mode)}`);
}

export function placeEtradeEquityOrder(userId, { symbol, side = "buy", qty = 1, mode = "sandbox" } = {}) {
    return apiFetch(`/api/brokers/etrade/order`, {
        method: "POST",
        body: JSON.stringify({ user_id: userId, symbol, side, qty, mode }),
    });
}

export async function getPutBotSettings() {
    return apiFetch("/api/put-bot/settings", { method: "GET" });
}

export async function savePutBotSettings(payload) {
    return apiFetch("/api/put-bot/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload || {}),
    });
}

export async function getPutBotStatus() {
    return apiFetch("/api/put-bot/status", { method: "GET" });
}

export async function getPutBotLogs(limit = 100) {
    return apiFetch(`/api/put-bot/logs?limit=${encodeURIComponent(limit)}`, { method: "GET" });
}

export async function scanPutBot(limit = 10) {
    return apiFetch("/api/put-bot/scan", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit }),
    });
}

export async function runPutBotTick({ execute = false, ignore_market_hours = false } = {}) {
    return apiFetch("/api/put-bot/run-tick", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ execute, ignore_market_hours }),
    });
}

export async function managePutBotExits({ execute = false, ignore_market_hours = false } = {}) {
    return apiFetch("/api/put-bot/manage-exits", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ execute, ignore_market_hours }),
    });
}

// ---------------- Admin Diagnostics Export ----------------
const DIAGNOSTICS_API_BASE =
    process.env.REACT_APP_API_BASE || "https://mystockpicks.onrender.com";

export function getAdminDiagnosticsCatalog(userId) {
    return apiFetch(
        `/api/admin/diagnostics/catalog?user_id=${encodeURIComponent(userId || "")}`
    );
}

function diagnosticsFilenameFromDisposition(disposition) {
    const value = String(disposition || "");
    const utf8 = value.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8?.[1]) {
        try {
            return decodeURIComponent(utf8[1].replace(/["']/g, ""));
        } catch (_) {
            return utf8[1].replace(/["']/g, "");
        }
    }
    const plain = value.match(/filename="?([^";]+)"?/i);
    return plain?.[1] || "mystockpicksai_diagnostics.zip";
}

function sleepDiagnostics(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function diagnosticsErrorMessage(response, fallback) {
    try {
        const payload = await response.clone().json();
        return payload?.detail || payload?.error || payload?.message || fallback;
    } catch (_) {
        try {
            const text = await response.text();
            return text ? text.slice(0, 700) : fallback;
        } catch (_) {
            return fallback;
        }
    }
}

async function diagnosticsFetch(path, options = {}, { retries = 2, timeoutMs = 30000 } = {}) {
    const token = getAuthToken();
    let lastError = null;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
        const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timer = controller
            ? window.setTimeout(() => controller.abort(), timeoutMs)
            : null;
        try {
            const response = await fetch(`${DIAGNOSTICS_API_BASE}${path}`, {
                ...options,
                headers: {
                    ...(options.headers || {}),
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                ...(controller ? { signal: controller.signal } : {}),
            });
            if (timer) window.clearTimeout(timer);

            // Retry only transient gateway/service failures. Auth and validation
            // errors should surface immediately with the backend's real message.
            if ([502, 503, 504].includes(response.status) && attempt < retries) {
                await sleepDiagnostics(700 * (attempt + 1));
                continue;
            }
            return response;
        } catch (err) {
            if (timer) window.clearTimeout(timer);
            lastError = err;
            if (attempt >= retries) break;
            await sleepDiagnostics(700 * (attempt + 1));
        }
    }

    const reason = lastError?.name === "AbortError"
        ? "request timed out"
        : (lastError?.message || "network request failed");
    throw new Error(`Diagnostics service connection failed after retries: ${reason}`);
}

export async function downloadAdminDiagnosticsZip(
    userId,
    {
        sections = [],
        start_date = "",
        end_date = "",
        mode = "all",
        timezone = "UTC",
        max_rows_per_table = 50000,
        onProgress = null,
    } = {}
) {
    const requestBody = {
        user_id: userId || "",
        sections,
        start_date: start_date || "",
        end_date: end_date || "",
        timezone: timezone || "UTC",
        mode: mode || "all",
        max_rows_per_table,
    };

    onProgress?.("Starting diagnostics export…");
    const prepareResponse = await diagnosticsFetch(
        "/api/admin/diagnostics/prepare",
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
        },
        { retries: 1, timeoutMs: 20000 }
    );

    if (!prepareResponse.ok) {
        const detail = await diagnosticsErrorMessage(
            prepareResponse,
            `Diagnostics prepare failed (${prepareResponse.status})`
        );
        throw new Error(detail);
    }

    const prepared = await prepareResponse.json();
    const jobId = prepared?.job_id;
    if (!jobId) throw new Error("Diagnostics backend did not return a job id.");

    const deadline = Date.now() + 5 * 60 * 1000;
    let status = prepared;
    while (Date.now() < deadline) {
        await sleepDiagnostics(900);
        const statusResponse = await diagnosticsFetch(
            `/api/admin/diagnostics/status/${encodeURIComponent(jobId)}`,
            { method: "GET" },
            { retries: 3, timeoutMs: 15000 }
        );
        if (!statusResponse.ok) {
            const detail = await diagnosticsErrorMessage(
                statusResponse,
                `Diagnostics status failed (${statusResponse.status})`
            );
            throw new Error(detail);
        }
        status = await statusResponse.json();
        const pct = Number(status?.progress || 0);
        const label = status?.message || "Building diagnostics ZIP…";
        onProgress?.(`${label}${Number.isFinite(pct) ? ` ${pct}%` : ""}`);

        if (status?.state === "ready") break;
        if (status?.state === "failed") {
            throw new Error(status?.error || "Diagnostics export failed on the server.");
        }
    }

    if (status?.state !== "ready") {
        throw new Error("Diagnostics export is still building after 5 minutes. Try again in a moment.");
    }

    onProgress?.("Downloading diagnostics ZIP…");
    const downloadResponse = await diagnosticsFetch(
        `/api/admin/diagnostics/download/${encodeURIComponent(jobId)}`,
        { method: "GET" },
        { retries: 3, timeoutMs: 120000 }
    );

    if (!downloadResponse.ok) {
        const detail = await diagnosticsErrorMessage(
            downloadResponse,
            `Diagnostics download failed (${downloadResponse.status})`
        );
        throw new Error(detail);
    }

    const blob = await downloadResponse.blob();
    if (!blob?.size) throw new Error("Diagnostics download returned an empty ZIP.");

    return {
        blob,
        filename: diagnosticsFilenameFromDisposition(
            downloadResponse.headers.get("Content-Disposition")
        ),
        size: blob.size,
        jobId,
    };
}

